import { ChangeDetectionStrategy, Component, HostBinding, inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BankverbindungHelper } from '@app/api/core/utils/helper/bankverbindung-helper';
import { ErrorMap } from '@app/shared/dynamic-form/dynamic-form.types';
import { FieldPropertyDescriptor } from '@app/shared/dynamic-form/util/descriptor/descriptor-types';
import { createDescriptorMap } from '@app/shared/dynamic-form/util/descriptor/descriptor-util';
import { FormatUppercasePipe } from '@app/shared/pipes/formatuppercase.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Bankverbindung } from '@o30/entities';
import { first } from 'rxjs/operators';
import { DescriptorMap } from '../../../dynamic-form/descriptor-map.model';
import { DynamicComponent } from '../../../dynamic-form/dynamic-component.model';
import { DynamicFormComponent } from '../../../dynamic-form/dynamic-form.component';
import { ValidatorConfig } from '../../../dynamic-form/validator-config.model';
import { FormatIbanPipe } from '../../../pipes/formatiban.pipe';
import { RemoveSpacePipe } from '../../../pipes/removespace.pipe';
import { DataService } from '../../../services/data/data.service';
import { MessageService } from '../../../services/ui/message/message.service';

@UntilDestroy()
@Component({
  selector:        'app-single-bankverbindung',
  templateUrl:     './single-bankverbindung.component.html',
  styleUrls:       [],
  standalone:      true,
  imports:         [DynamicFormComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SingleBankverbindungComponent implements DynamicComponent, // TODO: generic component (also refactor adresse...)
                                                      OnInit {
  dataService = inject(DataService);
  messageService = inject(MessageService);

  @Input()
  pacVisible = false;

  @Input()
  set kontotraegerName(newValue: string) {
    this._kontotraegerName = newValue;
  }

  get kontotraegerName(): string {
    return this._kontotraegerName;
  }

  private _kontotraegerName = '';

  @ViewChild(DynamicFormComponent, {static: true})
  private dynamicForm: DynamicFormComponent<Bankverbindung>;

  data: Bankverbindung;

  descriptorMap: DescriptorMap<Bankverbindung>;

  formControl: UntypedFormControl;

  private errors: ErrorMap; // TODO find out where this is needed
  @Input() styleClass: string;

  @HostBinding('class') get styleClasses(): string {
    let c = 'o30-form-control';
    if (this.styleClass) {
      c += ' ' + this.styleClass;
    }
    return c.trim();
  }

  ngOnInit() {
    // TODO: DRY
    if (!this.formControl) {
      this.formControl = new UntypedFormControl();
      console.warn('formcontrol not specified. this might result in unexpected behaviour.');
    }
    if (this.formControl.value) {
      this.data = JSON.parse(JSON.stringify(this.formControl.value));
      this.init();
    }
    this.formControl.valueChanges.pipe(
        untilDestroyed(this))
      .subscribe((newValue) => {
        this.data = newValue;
      });
    this.dynamicForm.errors$.pipe(
        untilDestroyed(this))
      .subscribe((errors) => this.errors = errors);

  }

  private init() {
    this.setDescriptorMap();
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--DynamicComponent implementation--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  dncInit(formControl: UntypedFormControl, validators: ValidatorConfig) {
    this.formControl = formControl;
    this.dynamicForm.valueChange$.pipe(
        untilDestroyed(this))
      .subscribe((newValue) => {
        if (newValue.key && newValue.key === 'iban' && newValue.value) {
          this.ibanChanged(newValue.value);
        }
        if (!this.data.name.value && this.kontotraegerName) {
          this.dynamicForm.setValue('name', this.kontotraegerName);
        }
        this.formControl.setValue(this.data);
      });
  }

  focus() {
    this.dynamicForm.focus('iban');
  }

  markAsTouched() {
    this.dynamicForm.markAllAsTouched();
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--private--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  private getLabels() {
    return {
      iban: 'IBAN',
      bic:  'BIC',
      name: 'Name'
    };
  }

  private setDescriptorMap() {
    const descriptor = {
      dsc: {
        iban: {
          context: {
            styleClass:      'input-30',
            formatPipe:      new FormatIbanPipe(),
            formatModelPipe: new RemoveSpacePipe()
          }
        },
        bic:  {
          context: {
            styleClass:      'input-30',
            formatPipe:      new FormatUppercasePipe(),
            formatModelPipe: new FormatUppercasePipe()
          }
        },
        name: {context: {styleClass: 'input-30'}}
      }
    } as FieldPropertyDescriptor<Bankverbindung>;
    this.descriptorMap = createDescriptorMap(descriptor, this.data, this.getLabels());
  }

  private ibanChanged(newValue: string): void {
    if (!this.data.bic.value && BankverbindungHelper.isAustrianIban(newValue)) {
      this.dataService.searchBic(newValue)
        .pipe(
          first())
        .subscribe((_payload) => {
          if (_payload.data && !_payload.errors && !_payload.businessErrors) {
            this.dynamicForm.setValue('bic', _payload.data);
          }
        });
    }
  }
}
